import { EXO_SKELETON_EVENT_NAMES } from './consts';
import { notInExoSkeletonError } from './internal';
import {
  ExoSkeletonRuntimeDataEvent,
  createPostMessageEventHandler,
  isInExoSkeleton,
  sendRuntimeDataEvent,
} from './runtime';

export const TRACKING_HOST_MESSAGE_EVENT_NAME =
  EXO_SKELETON_EVENT_NAMES.TRACKING_HOST_MESSAGE_EVENT_NAME;

export const TOTAL_REMOTE_LOADING_TIME = 'total-remote-loading-time';

export const TRACKING_ACTIONS = {
  START: 'start',
  END: 'end',
} as const;

type TrackingActions = typeof TRACKING_ACTIONS;
export type TrackingAction = TrackingActions[keyof TrackingActions];

export type TrackingMetadata = {
  displayName?: string;
  [key: string]: unknown;
};

export type TrackingData = {
  scope: string;
  moduleName: string;
  isRemoteAlreadyInitialized?: boolean;
  action: TrackingAction;
  trackingId: string;
  parentTrackingId?: string;
  metadata?: TrackingMetadata;
};

type RuntimeTrackingEvent = ExoSkeletonRuntimeDataEvent<
  typeof TRACKING_HOST_MESSAGE_EVENT_NAME,
  TrackingData
>;

export interface ICreateTrackingHostParameters {
  onTrack(event: RuntimeTrackingEvent): void;
}

function isTrackingDataEvent(data: unknown): data is RuntimeTrackingEvent {
  return (
    (data as RuntimeTrackingEvent)?.type === TRACKING_HOST_MESSAGE_EVENT_NAME
  );
}

export function createPerformanceTracker({
  onTrack,
}: ICreateTrackingHostParameters) {
  if (!isInExoSkeleton()) {
    throw notInExoSkeletonError({ source: 'createPerformanceTracker' });
  }

  return createPostMessageEventHandler(function ({
    data,
  }: MessageEvent<unknown>) {
    if (isTrackingDataEvent(data)) {
      onTrack(data);
    }
  });
}

export function trackLoadingTime(data: TrackingData) {
  if (!isInExoSkeleton()) {
    throw notInExoSkeletonError({ source: 'trackLoadingTime' });
  }

  const {
    scope,
    action,
    trackingId,
    parentTrackingId,
    moduleName,
    isRemoteAlreadyInitialized,
    metadata,
  } = data;

  if (!scope) {
    throw new Error(
      'Cannot execute "trackLoadingTime" without scope, please provide your scope i.e.: trackLoadingTime({ scope: "accMyScope", moduleName: "./App" })',
    );
  }

  if (!moduleName) {
    throw new Error(
      'Cannot execute "trackLoadingTime" without moduleName, please provide your moduleName i.e.: trackLoadingTime({ scope: "accMyScope", moduleName: "./App" })',
    );
  }

  return sendRuntimeDataEvent<
    typeof TRACKING_HOST_MESSAGE_EVENT_NAME,
    TrackingData
  >(TRACKING_HOST_MESSAGE_EVENT_NAME, {
    action,
    scope,
    isRemoteAlreadyInitialized,
    moduleName,
    trackingId,
    parentTrackingId,
    metadata,
  });
}
